import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
	OrdersTableTab,
} from '@ekhodealer/ekho-common';

import type {
	OrdersFacets,
	OrdersTableRow,	PaginatedResponse} from '@ekhodealer/ekho-common';

import { PLACEHOLDER } from '@common/lib/strings';

import { useSearchOrders } from '../api/useSearchOrders';
import { ordersParamsAtom } from '../atoms/ordersAtoms';

/**
 * Hook to fetch and calculate order counts for each tab
 * Returns a function to get the count for a specific tab as a string
 * PLACEHOLDER is returned if query is pending, a number string for actual count, and '0' only if result is actually 0
 */
export function useOrdersTableTabCounts(): (tab: OrdersTableTab) => string {
	// Get params from atom (includes search, tab filter, and facets)
	const tableParams = useAtomValue(ordersParamsAtom);

	// Fetch order counts for all tabs using facets (no tab filter for tab counts)
	const { data: ordersData, isPending } = useSearchOrders({
		filters: {
			...tableParams.filters,
			tabs: [], // No tab filter for tab counts
		},
		facets: ['externalStatus'],
		limit: 0, // We only need facets, not the data
	});

	// Calculate counts for each tab using transformed facets
	const getTabCount = useMemo(() => {
		return (tab: OrdersTableTab): string => {
			// If query is pending/loading, show PLACEHOLDER
			if (isPending) {
				return PLACEHOLDER;
			}

			const response = ordersData as PaginatedResponse<OrdersTableRow> | undefined;
			const facets = response?.facets as OrdersFacets | undefined;
			const orderTabCounts = facets?.orderTabCounts || {};
			const totalCount = response?.count ?? 0;

			if (tab === OrdersTableTab.ALL) {
				return totalCount.toLocaleString();
			}
			return (orderTabCounts[tab] ?? 0).toLocaleString();
		};
	}, [ordersData, isPending]);

	return getTabCount;
}
